package com.ijoin.ui.screens

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.WindowInsetsSides
import androidx.compose.foundation.layout.only
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Share
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import com.ijoin.data.model.Event
import com.ijoin.data.model.isFreeEvent
import com.ijoin.deeplink.DeepLinkBuilder
import com.ijoin.getPlatform
import com.ijoin.i18n.strings
import com.ijoin.platform.ShareHandler
import com.ijoin.ui.components.HtmlLandingView
import com.ijoin.ui.util.ButtonColors
import com.ijoin.ui.util.HtmlButtonColorExtractor

/**
 * Full-screen landing page for events with custom HTML content.
 * Shows a floating header with back, share, and book buttons.
 *
 * On web platforms, buttons are rendered as DOM elements above the iframe.
 * On mobile platforms, buttons are rendered with Compose UI.
 */
@Composable
fun LandingScreen(
    event: Event,
    onBackClick: () -> Unit,
    onBookClick: () -> Unit,
    onFormSubmit: ((Map<String, Any>) -> Unit)? = null,
    modifier: Modifier = Modifier
) {
    val strings = strings()
    val landingHtml = event.landingHtml ?: return
    val isWeb = getPlatform().isWeb
    val bookButtonText = if (event.isFreeEvent()) strings.bookFree else strings.book

    // Extract button colors from landing HTML, fallback to black bg / white text
    val buttonColors = remember(landingHtml) {
        HtmlButtonColorExtractor.extractButtonColors(landingHtml)
            ?: ButtonColors(background = Color.Black, text = Color.White)
    }

    Box(
        modifier = modifier
            .fillMaxSize()
            .windowInsetsPadding(WindowInsets.safeDrawing.only(WindowInsetsSides.Top))
    ) {
        // Full-screen HTML landing
        // On web: showHeader=true renders DOM buttons above iframe
        // On mobile: showHeader=false, Compose header below handles buttons
        HtmlLandingView(
            html = landingHtml,
            modifier = Modifier.fillMaxSize(),
            eventId = event.id,
            showHeader = isWeb,
            onBackClick = onBackClick,
            onBookClick = onBookClick,
            onShareClick = {
                val shareUrl = DeepLinkBuilder.eventUrl(event.id)
                ShareHandler.shareUrl(shareUrl, event.title)
            },
            bookButtonText = bookButtonText,
            onFormSubmit = onFormSubmit
        )

        // Floating header bar (only on mobile, web uses DOM buttons)
        if (!isWeb) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .align(Alignment.TopCenter)
                    .padding(16.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                // Back button (left)
                Surface(
                    shape = CircleShape,
                    color = Color.White.copy(alpha = 0.9f),
                    shadowElevation = 4.dp
                ) {
                    IconButton(onClick = onBackClick) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = strings.back,
                            tint = Color.Black
                        )
                    }
                }

                // Right group: Book + Share
                Row(
                    horizontalArrangement = Arrangement.spacedBy(12.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    // Book button (colors extracted from landing HTML or default black/white)
                    Button(
                        onClick = onBookClick,
                        colors = ButtonDefaults.buttonColors(
                            containerColor = buttonColors.background,
                            contentColor = buttonColors.text
                        ),
                        shape = RoundedCornerShape(20.dp),
                        elevation = ButtonDefaults.buttonElevation(defaultElevation = 4.dp),
                        contentPadding = PaddingValues(horizontal = 32.dp, vertical = 12.dp),
                        border = BorderStroke(2.dp, buttonColors.text.copy(alpha = 0.8f))
                    ) {
                        Text(
                            text = bookButtonText,
                            style = MaterialTheme.typography.labelLarge,
                            color = buttonColors.text
                        )
                    }

                    // Share button
                    Surface(
                        shape = CircleShape,
                        color = Color.White.copy(alpha = 0.9f),
                        shadowElevation = 4.dp
                    ) {
                        IconButton(
                            onClick = {
                                val shareUrl = DeepLinkBuilder.eventUrl(event.id)
                                ShareHandler.shareUrl(shareUrl, event.title)
                            }
                        ) {
                            Icon(
                                imageVector = Icons.Default.Share,
                                contentDescription = strings.share,
                                tint = Color.Black
                            )
                        }
                    }
                }
            }
        }
    }
}
